import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, filter, NEVER, Observable, Subscription, take, map } from 'rxjs';
import { Store } from '@ngrx/store';

import { PassengerNameRecord } from '@fcom/common-booking';
import { CmsArticle, CmsCollection, CmsImageData } from '@fcom/core-api';
import { NAME_PATTERN, PNR_PATTERN_WITH_TRAILING_WHITESPACE } from '@fcom/core/utils/utils';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  ButtonType,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  NotificationLayout,
  NotificationTheme,
  TabTheme,
} from '@fcom/ui-components';
import { TourOperatorCode } from '@fcom/dapi/api/models';
import { TourOperator } from '@fcom/common-booking/interfaces/tour-operator.interface';
import { AppState } from '@fcom/core/interfaces';
import { loginStatus, profileType } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx';
import { LoginStatus, ProfileType } from '@fcom/core-api/login';
import { unsubscribe } from '@fcom/core';

import { BookingDetails } from '../../interfaces/upcoming-trips.interface';
import { MmbWidgetTab } from '../../interfaces/pnr-widget-tab.interface';

/**
 * Shows a PNR Widget component with a background image, PNR login-form and displays error if data is incorrect.
 *
 * When provided, displays a cmsArticle block, tourOperator image, groupBookingModal and fcom-loader while waiting to retrieve the data
 *
 * Example usage:
 *
 * @example Inputs and Outputs
 * <fcom-pnr-widget
 *   [widgetTitle]="'labels.title'"
 *   [buttonText]="'login.submitText'"
 *   [backgroundImage]="backgroundImage"
 *   [tourOperatorImage]="tourOperatorImage"
 *   [operator]="operator"
 *   [loading]="loading"
 *   [disruptions$]="disruptions$"
 *   [cmsArticle$]="cmsArticle$"
 *   [error$]="mainError$"
 *   (formSubmit)="onSubmit($event)"
 *   (clearError)="closeErrorNotification()"
 *   (cancelRetrieve)="cancelRetrieve()">
 * </fcom-pnr-widget
 */

const widgetTabMap = {
  0: MmbWidgetTab.FIND_WITH_BOOKING_REFERENCE,
  1: MmbWidgetTab.UPCOMING_TRIPS,
};

const mapTourOperatorCode = (operator: TourOperator | undefined): TourOperatorCode | undefined => {
  switch (operator) {
    case TourOperator.APOLLO:
      return TourOperatorCode.KU;
    case TourOperator.ALPPIMATKAT:
      return TourOperatorCode.AL;
    case TourOperator.TJAREBORG:
      return TourOperatorCode.ST;
    case TourOperator.TOIVIOMATKAT:
      return TourOperatorCode.TM;
    case TourOperator.MIXXTRAVEL:
      return TourOperatorCode.MI;
    default:
      return undefined;
  }
};

@Component({
  selector: 'fcom-pnr-widget',
  templateUrl: './pnr-widget.component.html',
  styleUrls: ['./pnr-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PnrWidgetComponent implements OnInit, OnDestroy {
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly ButtonType = ButtonType;
  readonly IconPosition = IconPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly LoaderTheme = LoaderTheme;
  readonly ModalButtons = ModalButtons;
  readonly ButtonTheme = ButtonTheme;
  readonly MmbWidgetTab = MmbWidgetTab;
  readonly LoginStatus = LoginStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input() widgetTitle: string;
  @Input() buttonText: string;

  @Input() backgroundImage: CmsImageData;
  @Input() tourOperatorImage: CmsImageData;
  @Input() loading: boolean;
  @Input() operator: TourOperator;
  @Input() translationPrefix = 'loginFields';
  @Input() recLocRegex = PNR_PATTERN_WITH_TRAILING_WHITESPACE;
  @Input() prefilledRecLoc: string;
  @Input() prefilledLastName: string;
  @Input() checkForUpgradesTripLink = false;

  @Input() disruptions$: Observable<CmsCollection> = NEVER;
  @Input() personalizedDisruptions$: Observable<CmsCollection> = NEVER;
  @Input() error$: Observable<string>;
  @Input() cmsArticle$: Observable<CmsArticle>;

  @Output() formSubmit = new EventEmitter<PassengerNameRecord>();
  @Output() cancelRetrieve = new EventEmitter<void>();
  @Output() clearError = new EventEmitter<void>();

  showNoTripsNotification = signal(false);
  showErrorFetchingTripsNotification = signal(false);
  disableUpcomingTripsTab = signal(false);

  activeTabSubject$: BehaviorSubject<MmbWidgetTab> = new BehaviorSubject<MmbWidgetTab>(
    MmbWidgetTab.FIND_WITH_BOOKING_REFERENCE
  );
  activeTab$: Observable<MmbWidgetTab> = this.activeTabSubject$.asObservable();
  isLoginStatusVerificationLoaderOn$: Observable<boolean>;
  loginStatus$: Observable<LoginStatus>;
  profileType$: Observable<ProfileType>;
  showMyTripsToLoggedUser$: Observable<boolean>;

  loginForm: FormGroup<{ lastName: FormControl<string>; recLoc: FormControl<string> }>;

  NAME_VALIDATORS = [
    Validators.required,
    Validators.minLength(2),
    Validators.maxLength(50),
    Validators.pattern(NAME_PATTERN),
  ];

  private subscriptions = new Subscription();

  constructor(private store$: Store<AppState>) {}

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.profileType$ = this.store$.pipe(profileType());

    this.showMyTripsToLoggedUser$ = combineLatest([this.loginStatus$, this.profileType$]).pipe(
      map(([loginStatus, profileType]) => loginStatus === LoginStatus.LOGGED_IN && profileType === ProfileType.FPLUS)
    );
    const recLoc = new FormControl(this.prefilledRecLoc ?? '', [Validators.required]);

    if (!this.operator) {
      recLoc.addValidators(Validators.pattern(this.recLocRegex));
    } else {
      recLoc.addValidators(Validators.pattern(/^[0-9]+$/));
    }

    this.loginForm = new FormGroup({
      lastName: new FormControl(this.prefilledLastName ?? '', this.NAME_VALIDATORS),
      recLoc,
    });
    this.isLoginStatusVerificationLoaderOn$ = this.loginStatus$.pipe(
      map((status: LoginStatus) => !(status !== LoginStatus.INITIAL && status !== LoginStatus.PENDING))
    );
    this.subscriptions.add(
      this.profileType$
        .pipe(filter((profileType) => profileType === ProfileType.CORPORATE))
        .subscribe(() => this.disableUpcomingTripsTab.set(true))
    );
    this.subscriptions.add(
      this.showMyTripsToLoggedUser$
        .pipe(
          filter((showMyTrips) => showMyTrips),
          take(1)
        )
        .subscribe(() => {
          this.disableUpcomingTripsTab.set(false);
          this.activeTabSubject$.next(widgetTabMap[MmbWidgetTab.UPCOMING_TRIPS]);
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.formSubmit.emit(this.getForm());
    } else {
      this.loginForm.markAsDirty();
      this.loginForm.markAllAsTouched();
      this.loginForm.updateValueAndValidity();
    }
  }

  viewBookingDetails(bookingDetails: BookingDetails): void {
    this.prefillFormDetails(bookingDetails);
    this.onSubmit();
  }

  private prefillFormDetails(bookingDetails: BookingDetails): void {
    this.loginForm.controls.recLoc.setValue(bookingDetails.recLoc);
    this.loginForm.controls.lastName.setValue(bookingDetails.lastName);
  }

  setActiveTab({ index }: { index: number; data?: unknown }): void {
    this.activeTabSubject$.next(widgetTabMap[index]);
  }

  onNoTripsNotification(activateNotification: boolean): void {
    this.showNoTripsNotification.set(activateNotification);
    this.activeTabSubject$.next(widgetTabMap[1]);
  }

  onErrorFetchingTripsNotification(errorFetchingTrips: boolean): void {
    this.showErrorFetchingTripsNotification.set(errorFetchingTrips);
    this.disableUpcomingTripsTab.set(errorFetchingTrips);
    this.activeTabSubject$.next(widgetTabMap[0]);
  }

  private getForm(): PassengerNameRecord {
    return {
      recLoc: this.loginForm.controls.recLoc.value.trim(),
      lastName: this.loginForm.controls.lastName.value.trim(),
      tourOperatorCode: mapTourOperatorCode(this.operator),
    };
  }

  protected readonly TabTheme = TabTheme;
}
